// Column rendering tasks for a specific date in the calendar.
import { format } from 'date-fns';
import { useMemo, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import useDayTasks from '@/features/planning/hooks/time-grid/useDayTasks';
import useCurrentTimePosition from '@/features/planning/hooks/time-grid/useCurrentTimePosition';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { useTaskMutationsContext } from '@/features/tasks/context/useTaskMutationsContext';
import { useAuth } from '@/features/auth/useAuth';
import { useGCal } from '@/features/planning/context/useGCal';

import ScheduledTask from '@/features/planning/components/time-grid/ScheduledTask';
import TimeSlot from '@/features/planning/components/time-grid/TimeSlot';
import GCalEventOverlay from '@/features/planning/components/time-grid/GCalEventOverlay';
import GridTaskItem from '@/features/planning/components/shared/GridTaskItem';
import DayColumnHeader from '@/features/planning/components/time-grid/DayColumnHeader';
import DayColumnNowLine from '@/features/planning/components/time-grid/DayColumnNowLine';
import { isMainTask } from '@/features/tasks/components/atoms/taskMetadataViewState';
import { isOnboardingTimeSlotHighlighted } from '@/features/onboarding/utils/onboardingTimeSlotHighlight';

const DayColumn = ({
  day,
  fifteenMinuteSlots,
  scheduledTasks,
  currentTaskId,
  activeTaskId,
  onDoubleClick,
  onSaveNewTask,
  onCancelNewTask,
  fifteenMinuteSlotHeight,
  startHour,
  endHour,
  onboardingTimeSlotHighlight = null,
  onboardingResizeHighlightTaskId = null,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleToggleComplete } = useTaskMutationsContext();
  const { tasksMap } = useTasksContext();
  const { isEnabled, getEventsForRange } = useGCal();
  const timeSlotsContainerRef = useRef(null);

  const { tasksForDay, alldayTasks = [] } = useDayTasks({
    day,
    scheduledTasks,
    fifteenMinuteSlotHeight,
    startHour,
  });

  const gcalEvents = useMemo(() => {
    const dayStart = new Date(day.date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day.date);
    dayEnd.setHours(23, 59, 59, 999);
    return getEventsForRange(dayStart, dayEnd);
  }, [day, getEventsForRange]);

  const isToday =
    format(day.date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
  const timePosition = useCurrentTimePosition(
    fifteenMinuteSlotHeight,
    startHour,
    endHour,
  );
  const topPosition = timePosition;

  const handleDoubleClick = (e) => {
    if (e.target.closest('[data-scheduled-task]')) return;
    if (!timeSlotsContainerRef.current || fifteenMinuteSlotHeight === 0) return;

    const containerTop =
      timeSlotsContainerRef.current.getBoundingClientRect().top;
    const clickY = e.clientY - containerTop;
    const slotIndex = Math.floor(clickY / fifteenMinuteSlotHeight);
    const clickedTime = fifteenMinuteSlots[slotIndex];

    if (clickedTime) {
      onDoubleClick(day, clickedTime);
    }
  };

  return (
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 150,
        alignSelf: 'flex-start',
        borderWidth: 'var(--plan-day-column-border)',
        borderStyle: 'solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        height: 'auto',
      }}
      data-testid="day-column"
    >
      <DayColumnHeader day={day} />
      <Box
        data-testid="allday-task-row"
        sx={{
          height: 'var(--plan-allday-row-height)',
          boxSizing: 'border-box',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
          p: '3px',
          flexShrink: 0,
          overflowY: 'auto',
        }}
      >
        {alldayTasks.map((task) => (
          <GridTaskItem
            key={task.id}
            task={task}
            showComplete={!isMainTask(task, tasksMap)}
            roomy
            keyboardActive={activeTaskId === task.id}
            onToggleComplete={(checked) => {
              if (user) handleToggleComplete(task, checked);
            }}
          />
        ))}
      </Box>
      <Box
        ref={timeSlotsContainerRef}
        onDoubleClick={handleDoubleClick}
        sx={{
          position: 'relative',
          height: 'var(--total-column-height)',
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        {fifteenMinuteSlots.map((slotTime) => (
          <TimeSlot
            key={slotTime}
            time={slotTime}
            date={day.date}
            onboardingHighlight={isOnboardingTimeSlotHighlighted(
              onboardingTimeSlotHighlight,
              day.date,
              slotTime,
            )}
          />
        ))}
        {tasksForDay.map((task) => (
          <ScheduledTask
            key={task.id}
            task={task}
            style={task.style}
            fifteenMinuteSlotHeight={fifteenMinuteSlotHeight}
            navigate={navigate}
            currentTaskId={currentTaskId}
            keyboardActive={activeTaskId === task.id}
            onSave={onSaveNewTask}
            onCancel={onCancelNewTask}
            onboardingHighlightResize={
              onboardingResizeHighlightTaskId !== null &&
              onboardingResizeHighlightTaskId !== undefined
                ? task.id === onboardingResizeHighlightTaskId
                : false
            }
          />
        ))}
        <GCalEventOverlay
          day={day}
          fifteenMinuteSlotHeight={fifteenMinuteSlotHeight}
          events={gcalEvents}
          isEnabled={isEnabled}
          startHour={startHour}
        />
        {topPosition >= 0 ? (
          <DayColumnNowLine topPosition={topPosition} isToday={isToday} />
        ) : null}
      </Box>
    </Box>
  );
};

export default memo(DayColumn);
